// Shared types for buildings and financial summary

import { Address } from "./Address";
import { GeoLocation, LocationIdentifiers } from "./GeoLocation";


export enum BuildingRole {
  Admin = 'admin',
  Manager = 'manager',
  Resident = 'resident',
  Viewer = 'viewer',
}

export enum ServiceChargeFrequency {
  Monthly = 'monthly',
  Quarterly = 'quarterly',
  Annually = 'annually',
}
export interface Building {
  id: string; // UUID
  ownerId: string;
  /**
   * Legacy free-text address. Retained for backward compatibility (Req 11.1,
   * 11.2). New code should prefer `structuredAddress`. Legacy-only records
   * remain readable and continue to work.
   */
  address: string;
  /** Structured postal address — additive, optional (address-geolocation R11). */
  structuredAddress?: Address;
  /** Canonical physical location — additive, optional. */
  location?: GeoLocation;
  /** Derived identifiers (never canonical) — regenerable from `location`. */
  locationIdentifiers?: LocationIdentifiers;
  contactEmail?: string;
  contactPhone?: string;
  createdAt: string;
  description?: string;
  managementCompany?: string;
  name: string;
  /**
   * Notification preferences for the building's Settings tab.
   * Additive, optional (buildings Rule 20). Existing records that omit
   * the field are treated by the UI as "all preferences default-on" —
   * this preserves back-compat for records that predate the field.
   *
   * The closed set of keys is exactly the three below; the backend
   * rejects PUT bodies that supply a preference key outside this set
   * with a 400 so accidental typos don't silently persist.
   */
  notificationPreferences?: {
    statementIssued: boolean;
    expenseCreated: boolean;
    paymentReminder: boolean;
  };
  numberOfUnits: number;
  role: BuildingRole;
  serviceChargeFrequency: ServiceChargeFrequency;
  /**
   * Building archive status. When `'archived'`, the building is hidden
   * from list views but every record remains readable — matches the
   * Danger Zone copy: "All data will be preserved and can be restored
   * later" (buildings Rule 20). Absent / undefined means active.
   */
  status?: 'active' | 'archived';
  unitsCount: number;
  updatedAt?: string;
  /**
   * @deprecated Use `builtDate` for new writes. Retained for backward
   * compatibility on records that predate the `builtDate` field.
   */
  yearBuilt?: number;
  /**
   * When the building was originally constructed. ISO 8601 `YYYY-MM-DD`.
   * Additive, optional — records that predate this field remain readable.
   */
  builtDate?: string;
  /**
   * When the current management company (i.e. the platform user) started
   * managing this building. ISO 8601 `YYYY-MM-DD`. Additive, optional.
   *
   * Semantics: the default anchor date for anything that measures
   * management duration — resident move-in defaults on assignment,
   * management-fee accrual, occupancy periods, etc. When a Unit carries
   * its own `managementStartDate`, the unit's value takes precedence;
   * when absent, the building-level value applies.
   */
  managementStartDate?: string;
}


export interface FinancialSummary {
  currentBalance: number;
  totalIncome: number;
  totalBills: number;
  reserveFund: number;
  budgetCompliance: number; // Percentage
  outstandingPayments: number;
}
